import React from 'react'

import {
    StatCard,
    VsIncreaseIcon,
    VsPaymentIcon,
    UiDashboardIcon,
    VsReceiptIcon,
} from '@octane/spark'
import getVehicleTypeIcon, { VehicleType } from 'utils/getVehicleTypeIcon'

import LegendYearOverYearOriginationsChart from '../LegendYearOverYearOriginationsChart'
import { DashboardItemProps } from '../types'
import {
    BoardContainer,
    BottomCards,
    ChartContainer,
    MonthlyTitle,
    TopCards,
} from './LegendBoard.styled'

interface VehicleTypes {
    [key: string]: string
}

type recentMonthsComparisonType = {
    date: Date
    dealershipId: number
    fundedCount: number
    originations: number
    popularVehicleType: string
    averageOrigination: number
    averageOriginationFico: string
}

type StatCardColor = keyof typeof StatCard.color

const formatDateAsMonthAndYear = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'long',
    })
}

const formatCurrency = (number: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(number)
}

function LegendBoard({ componentStyle, data }: Readonly<DashboardItemProps>) {
    const colors = ['orange', 'primary', 'tangerine']

    const vehicleTypes: VehicleTypes = {
        'sports bike': 'Sports',
        'dirt bike': 'Dirt',
        'electric bike': 'Electric',
        'go kart': 'GoKart',
        'golf cart': 'Golf',
        'marine engine': 'Marine',
        pwc: 'PWC',
        'pontoon hybrid': 'Pontoon',
        snowmobile: 'Snow',
    }

    return (
        <BoardContainer componentStyle={componentStyle}>
            <TopCards>
                <StatCard
                    stat={formatCurrency(data.totalFundings.currentMonth.data)}
                    heading={data.totalFundings.currentMonth.title}
                    fullWidth
                    icon={VsIncreaseIcon}
                    color="orange"
                    textColor="jetlineBlack"
                />
                <StatCard
                    stat={formatCurrency(data.totalFundings.allTimeMonth.data)}
                    heading={data.totalFundings.allTimeMonth.title}
                    fullWidth
                    icon={VsPaymentIcon}
                    color="orange"
                    textColor="jetlineBlack"
                />
            </TopCards>
            <ChartContainer>
                <LegendYearOverYearOriginationsChart
                    data={data.originations.data}
                />
            </ChartContainer>
            {data.recentMonthsComparison.map(
                (item: recentMonthsComparisonType, index: number) => {
                    const VehicleIcon = getVehicleTypeIcon(
                        item.popularVehicleType as VehicleType,
                    )

                    return (
                        <div key={item.date.toString()}>
                            <MonthlyTitle>
                                {formatDateAsMonthAndYear(item.date)}
                            </MonthlyTitle>
                            <BottomCards>
                                <StatCard
                                    icon={VsPaymentIcon}
                                    heading="Total Origination"
                                    stat={formatCurrency(item.originations)}
                                    size="small"
                                    color={colors[index] as StatCardColor}
                                    fullWidth
                                    textColor="jetlineBlack"
                                />
                                <StatCard
                                    icon={UiDashboardIcon}
                                    heading="Average Fico"
                                    stat={item.averageOriginationFico}
                                    size="small"
                                    color={colors[index] as StatCardColor}
                                    fullWidth
                                    textColor="jetlineBlack"
                                />
                                <StatCard
                                    icon={VehicleIcon}
                                    heading="Popular Unit"
                                    stat={
                                        vehicleTypes[item.popularVehicleType] ||
                                        item.popularVehicleType
                                    }
                                    size="small"
                                    color={colors[index] as StatCardColor}
                                    fullWidth
                                    textColor="jetlineBlack"
                                />
                                <StatCard
                                    icon={VsReceiptIcon}
                                    heading="Avg. Funding"
                                    stat={formatCurrency(
                                        item.averageOrigination,
                                    )}
                                    size="small"
                                    color={colors[index] as StatCardColor}
                                    fullWidth
                                    textColor="jetlineBlack"
                                />
                            </BottomCards>
                        </div>
                    )
                },
            )}
        </BoardContainer>
    )
}

export default LegendBoard
